import { useEffect, useRef, useState } from 'react';
import { useFormApi, useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getMessageWithPlaceholders } from 'shared-locales';
import { StatCount } from 'components/common/stats';
import {
  Box,
  BoxTable,
  BoxTableHeader,
  BoxTableCell,
  BoxTableRow
} from 'components/common/box';
import { Checkbox, FormGroup, NumberStepper, Password } from '.';

type FormValues = {
  commonPassword: boolean;
  expirationInDays: number[];
  expirationInDaysAll: number;
  passwords: string[];
  passwordsAll: string;
  usernames: string[];
};

interface Props {
  users: string[];
}

const UserSelectorNewUsers = ({ users }: Props) => {
  const {
    messages,
    serverConfig: {
      passwordMaximumLength,
      passwordDoNotForcePasswordChange,
      passwordManuallySetExpirationTimeInDays
    },
    theme
  } = useReduxState();

  const formApi = useFormApi();
  const formState = useFormState<FormValues>();

  const passwordTimeout = useRef<NodeJS.Timeout>(undefined);

  const [userIndex, setUserIndex] = useState<number[]>([]);

  useEffect(() => {
    const { commonPassword, expirationInDaysAll, passwordsAll } =
      formState.values;

    const indexes = getUserIndexes();
    if (commonPassword && passwordsAll) {
      users.forEach((user, i) => {
        formApi.setValue(`passwords.${indexes[i]}`, passwordsAll);
        formApi.setValue(`expirationInDays.${indexes[i]}`, expirationInDaysAll);
      });
    }
  }, [users]);

  useEffect(() => {
    getUserIndexes();
  }, [formState?.values?.usernames]);

  useEffect(() => {
    const { commonPassword, passwordsAll } = formState.values;

    clearTimeout(passwordTimeout.current);
    if (commonPassword)
      passwordTimeout.current = setTimeout(() => {
        users.forEach((user, i) =>
          formApi.setValue(`passwords[${userIndex[i]}]`, passwordsAll)
        );
      }, 250);
  }, [formState.values?.passwordsAll]);

  const getUserIndexes = () => {
    const { usernames } = formState.values;
    if (!usernames) return;

    const indexes = users.map((user) => usernames.indexOf(user));
    setUserIndex(indexes);
    return indexes;
  };

  const handleCommonPassword = (value: string) => {
    if (value) {
    } else {
      formApi.setValue('passwordsAll', undefined);
      formApi.setError('passwordsAll', undefined);
    }
  };

  const handleExpirationTimeInDaysChange = (time: number) => {
    setTimeout(() => {
      if (formState?.values?.commonPassword) {
        users.forEach((user, i) =>
          formApi.setValue(`expirationInDays[${userIndex[i]}]`, time)
        );
      }
    });
  };

  const validateMaxPasswordLength = (value: string) => {
    if (value.length > passwordMaximumLength) {
      return getMessageWithPlaceholders({
        message: messages.authPasswordLengthErrorMax,
        placeholders: { length: passwordMaximumLength }
      });
    }
  };

  const formError = formState.errors?.passwords;

  return (
    <FormGroup maxWidth="100%">
      <div>
        <Box
          headerContent={
            <StatCount
              category="success-light"
              isLarge={true}
              label={messages.userSelectorNewUser}
              value={users.length}
            />
          }
          padding={0}
          style={{
            ...(formError && { border: `1px solid ${theme.dangerColor}` })
          }}
          subHeaderContent={
            <div className="common-password">
              <div className="common-password-checkbox">
                <Checkbox
                  field="commonPassword"
                  id="commonPassword"
                  label={messages.userSelectorSetPassword}
                  onValueChange={handleCommonPassword}
                />
              </div>

              <div className="common-password-container">
                <Password
                  disabled={!formState.values.commonPassword}
                  field="passwordsAll"
                  formApi={formApi}
                  id="passwords-all"
                  showGenerateOption={true}
                  showPasswordInitial={true}
                  validateAdditional={validateMaxPasswordLength}
                  {...(formState?.values?.commonPassword
                    ? { required: true }
                    : { required: false })}
                />
              </div>

              {!passwordDoNotForcePasswordChange && (
                <div className="common-password-expiration">
                  <div className="password-expiration-label">
                    {messages.commonValidFor}
                  </div>
                  <NumberStepper
                    disabled={!formState.values.commonPassword}
                    field="expirationInDaysAll"
                    id={`expiration-in-days-all`}
                    initialValue={passwordManuallySetExpirationTimeInDays}
                    label={messages.commonDays}
                    max={100000}
                    min={passwordManuallySetExpirationTimeInDays}
                    onValueChange={handleExpirationTimeInDaysChange}
                    stepValue={1}
                    style={{ textTransform: 'lowercase' }}
                  />
                </div>
              )}
            </div>
          }
        >
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonUsername}</BoxTableCell>
              <BoxTableCell>{messages.commonPassword}</BoxTableCell>
            </BoxTableHeader>

            {users.map((user, i) => (
              <BoxTableRow key={user}>
                <BoxTableCell>{user}</BoxTableCell>

                <BoxTableCell shrink={true}>
                  <div className="password-wrapper">
                    <div className="password-container">
                      <Password
                        disabled={formState.values.commonPassword}
                        field={`passwords[${userIndex[i]}]`}
                        formApi={formApi}
                        id={`passwords-${user}`}
                        showGenerateOption={true}
                        showPasswordInitial={true}
                        validateAdditional={validateMaxPasswordLength}
                        {...(!formState?.values?.commonPassword
                          ? { required: true }
                          : { required: false })}
                      />
                    </div>

                    {!passwordDoNotForcePasswordChange && (
                      <div className="password-expiration">
                        <div className="password-expiration-label">
                          {messages.commonValidFor}
                        </div>
                        <NumberStepper
                          disabled={formState.values.commonPassword}
                          field={`expirationInDays[${userIndex[i]}]`}
                          id={`expiration-in-days-${user}`}
                          initialValue={passwordManuallySetExpirationTimeInDays}
                          label={messages.commonDays}
                          max={100000}
                          min={passwordManuallySetExpirationTimeInDays}
                          stepValue={1}
                          style={{ textTransform: 'lowercase' }}
                        />
                      </div>
                    )}
                  </div>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        </Box>
      </div>

      <style jsx>{`
        .common-password {
          width: 100%;
        }
        .common-password-container,
        .password-container {
          width: 200px;
        }
        .common-password-container,
        .common-password-expiration {
          margin: 8px 0 0 23px;
        }
        .password-expiration {
          margin: 8px 0 0;
        }
        .common-password-expiration,
        .password-expiration {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .password-expiration-label {
          font-weight: 400;
          white-space: nowrap;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .common-password {
            align-items: center;
            display: flex;
            gap: 30px;
          }
          .password-wrapper {
            align-items: center;
            display: flex;
            gap: 30px;
          }
          .common-password-checkbox {
            flex-grow: 1;
          }
          .common-password-container,
          .common-password-expiration,
          .password-expiration {
            margin: 0;
          }
          .common-password-container,
          .password-container {
            width: 270px;
          }
        }
      `}</style>
    </FormGroup>
  );
};

export default UserSelectorNewUsers;
